import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { AuthContext } from './authContext'
import { api, getStoredToken, setStoredToken, clearStoredToken } from '../api/client'

const INACTIVITY_MS = 60 * 60 * 1000 // 60 Minuten

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => getStoredToken())
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const inactivityTimerRef = useRef(null)

  useEffect(() => {
    if (!token) {
      setLoading(false)
      return
    }
    api.get('/auth/me')
      .then((r) => setUser(r.data))
      .catch(() => {
        setToken(null)
        setUser(null)
        clearStoredToken()
      })
      .finally(() => setLoading(false))
  }, [token])

  const logout = useCallback(() => {
    setToken(null)
    setUser(null)
    clearStoredToken()
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current)
      inactivityTimerRef.current = null
    }
  }, [])

  useEffect(() => {
    if (!token) return
    const resetTimer = () => {
      if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current)
      inactivityTimerRef.current = setTimeout(() => {
        logout()
        window.location.replace('/login')
      }, INACTIVITY_MS)
    }
    resetTimer()
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart']
    events.forEach((e) => window.addEventListener(e, resetTimer))
    return () => {
      if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current)
      events.forEach((e) => window.removeEventListener(e, resetTimer))
    }
  }, [token, logout])

  const login = useCallback(async (email, password, rememberMe = true) => {
    const body = new URLSearchParams()
    body.set('username', email)
    body.set('password', password)
    body.set('remember_me', rememberMe ? 'true' : 'false')
    const { data } = await api.post('/auth/login', body)
    if (!data?.access_token) throw new Error('Kein Token erhalten')
    const tokenStr = data.access_token
    setToken(tokenStr)
    setStoredToken(tokenStr, rememberMe)
    try {
      const payload = JSON.parse(atob(tokenStr.split('.')[1]))
      setUser({ id: 0, email: payload.sub || email, is_active: true })
    } catch {
      const me = await api.get('/auth/me')
      setUser(me.data)
    }
    return { email }
  }, [])

  const refreshUser = useCallback(() => {
    if (!token) return
    api.get('/auth/me').then((r) => setUser(r.data)).catch(() => {})
  }, [token])

  const value = useMemo(
    () => ({ token, user, loading, login, logout, refreshUser }),
    [token, user, loading, login, logout, refreshUser]
  )

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}
